// src/components/quiz/QuestionCard.tsx
import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { QuestionDefinition, Answer } from '../../lib/questions'
import type { Section } from '../../hooks/useQuizState'

interface QuestionCardProps {
  question: QuestionDefinition
  section: Section
  sectionTitle: string
  selectedAnswer?: Answer
  existingMultiSelect?: Set<string>
  onAnswer: (answerKey: string, answerLabel: string) => void
  onMultiToggle: (answerKey: string, answerLabel: string) => void
  onMultiConfirm: () => void
  onSkip?: () => void
  onBack?: () => void
  canGoBack?: boolean
  isWarmSection?: boolean
}

export function QuestionCard({
  question,
  section,
  sectionTitle,
  existingMultiSelect,
  selectedAnswer,
  onAnswer,
  onMultiToggle,
  onMultiConfirm,
  onSkip,
  onBack,
  canGoBack,
  isWarmSection,
}: QuestionCardProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(selectedAnswer?.answerKey || null)
  const [confirmed, setConfirmed] = useState(false)
  const [numberValue, setNumberValue] = useState<string>(
    question.type === 'number' ? (selectedAnswer?.answerKey || '') : ''
  )
  const [locCounty, setLocCounty] = useState('')
  const [locCountry, setLocCountry] = useState('')

  useEffect(() => {
    setSelectedKey(selectedAnswer?.answerKey || null)
    setConfirmed(false)
    setNumberValue(question.type === 'number' ? (selectedAnswer?.answerKey || '') : '')
    setLocCounty('')
    setLocCountry('')
  }, [question.id, selectedAnswer?.answerKey, question.type])

  const handleSingleSelect = (key: string, label: string) => {
    if (confirmed) return
    setSelectedKey(key)
    // Auto-advance after 400ms
    setTimeout(() => {
      setConfirmed(true)
      setTimeout(() => onAnswer(key, label), 100)
    }, 400)
  }

  const multiSelected = existingMultiSelect || new Set<string>()
  const hasMultiSelection = multiSelected.size > 0

  return (
    <div style={{
      minHeight: '100dvh',
      background: isWarmSection ? 'var(--color-warm-bg)' : 'var(--color-cream)',
      display: 'flex',
      flexDirection: 'column',
      padding: '1.5rem',
      transition: 'background 0.4s ease',
    }}>
      {/* Section badge */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {canGoBack && onBack && (
          <button
            onClick={(e) => { e.preventDefault(); onBack() }}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-muted)',
              fontSize: '0.7rem',
              fontWeight: 600,
              cursor: 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            ← Back <span style={{ opacity: 0.3, paddingLeft: '0.5rem' }}>|</span>
          </button>
        )}
        <span style={{
          fontSize: '0.7rem',
          fontWeight: 600,
          color: 'var(--color-muted)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}>
          {section} · {sectionTitle}
        </span>
      </div>

      {/* Question */}
      <motion.h2
        key={question.id + '-question'}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'clamp(1.1rem, 3.5vw, 1.375rem)',
          color: 'var(--color-deep-teal)',
          lineHeight: 1.4,
          marginBottom: '1.75rem',
          maxWidth: '600px',
        }}
      >
        {question.text}
      </motion.h2>

      {/* Number input — rendered in place of options for 'number' type */}
      {question.type === 'number' && question.numberOptions && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          style={{ maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '1rem' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <input
              type="number"
              min={question.numberOptions.min}
              max={question.numberOptions.max}
              placeholder={question.numberOptions.placeholder || ''}
              value={numberValue}
              onChange={e => setNumberValue(e.target.value)}
              style={{
                width: '120px',
                padding: '0.75rem 1rem',
                fontSize: '1.25rem',
                fontFamily: 'var(--font-sans)',
                border: '1.5px solid rgba(148,163,184,0.4)',
                borderRadius: 'var(--radius-xl)',
                background: 'var(--color-white)',
                color: 'var(--color-deep-teal)',
                outline: 'none',
                textAlign: 'center',
                appearance: 'textfield',
              }}
              onFocus={e => (e.currentTarget.style.borderColor = 'var(--color-teal)')}
              onBlur={e => (e.currentTarget.style.borderColor = 'rgba(148,163,184,0.4)')}
            />
            <span style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '0.95rem',
              color: 'var(--color-muted)',
            }}>
              {question.numberOptions.unit}
            </span>
          </div>

          {question.numberOptions.hint && (
            <p style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '0.8rem',
              color: 'var(--color-muted)',
              lineHeight: 1.5,
              margin: 0,
            }}>
              {question.numberOptions.hint}
            </p>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
            <motion.button
              whileHover={numberValue !== '' ? { scale: 1.02 } : {}}
              whileTap={numberValue !== '' ? { scale: 0.98 } : {}}
              disabled={numberValue === ''}
              onClick={() => {
                const v = Number(numberValue)
                if (!isNaN(v)) onAnswer(String(v), `${v} ${question.numberOptions!.unit}`)
              }}
              style={{
                flex: 1,
                padding: '0.875rem',
                background: numberValue !== '' ? 'var(--color-deep-teal)' : 'rgba(148,163,184,0.25)',
                color: numberValue !== '' ? 'white' : 'var(--color-muted)',
                border: 'none',
                borderRadius: 'var(--radius-xl)',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: numberValue !== '' ? 'pointer' : 'not-allowed',
                fontFamily: 'var(--font-sans)',
                transition: 'background 0.2s, color 0.2s',
              }}
            >
              Continue →
            </motion.button>

            {question.numberOptions.optional && onSkip && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onSkip}
                style={{
                  padding: '0.875rem 1.25rem',
                  background: 'transparent',
                  color: 'var(--color-muted)',
                  border: '1.5px solid rgba(148,163,184,0.3)',
                  borderRadius: 'var(--radius-xl)',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                Skip
              </motion.button>
            )}
          </div>
        </motion.div>
      )}

      {/* Location picker */}
      {question.type === 'location' && question.locationOptions && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          style={{ maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}
        >
          {question.locationOptions.featured.map((f) => {
            const isSelected = selectedKey === f.key
            return (
              <motion.button
                key={f.key}
                whileHover={!selectedKey ? { scale: 1.01, borderColor: 'var(--color-teal)' } : {}}
                whileTap={{ scale: 0.99 }}
                animate={{ opacity: selectedKey && !isSelected ? 0.4 : 1, scale: isSelected ? 1.01 : 1 }}
                onClick={() => handleSingleSelect(f.key, f.label)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.875rem 1rem',
                  background: isSelected ? 'rgba(13,148,136,0.06)' : 'var(--color-white)',
                  border: `1.5px solid ${isSelected ? 'var(--color-teal)' : 'rgba(148,163,184,0.3)'}`,
                  borderRadius: 'var(--radius-xl)',
                  cursor: 'pointer', textAlign: 'left',
                  fontFamily: 'var(--font-sans)', fontSize: '0.925rem',
                  color: isSelected ? 'var(--color-deep-teal)' : 'var(--color-slate)',
                  fontWeight: isSelected ? 500 : 400,
                  minHeight: '48px',
                  transition: 'background 0.2s, border-color 0.2s, color 0.2s',
                }}
              >
                <span style={{
                  width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${isSelected ? 'var(--color-teal)' : 'rgba(148,163,184,0.5)'}`,
                  background: isSelected ? 'var(--color-teal)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {isSelected && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff' }} />}
                </span>
                {f.label}
              </motion.button>
            )
          })}

          {/* Other county in Kenya */}
          <div style={{ position: 'relative' }}>
            <select
              value={locCounty}
              onChange={e => {
                const opt = question.locationOptions!.counties.find(c => c.key === e.target.value)
                if (opt) { setLocCounty(opt.key); handleSingleSelect(opt.key, opt.label) }
              }}
              style={{
                width: '100%', padding: '0.875rem 2.5rem 0.875rem 1rem',
                background: 'var(--color-white)',
                border: `1.5px solid ${locCounty ? 'var(--color-teal)' : 'rgba(148,163,184,0.3)'}`,
                borderRadius: 'var(--radius-xl)',
                fontFamily: 'var(--font-sans)', fontSize: '0.925rem',
                color: locCounty ? 'var(--color-deep-teal)' : 'var(--color-slate)',
                cursor: 'pointer', appearance: 'none', outline: 'none',
                minHeight: '48px',
              }}
            >
              <option value="">Another county in Kenya…</option>
              {question.locationOptions.counties.map(c => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <span style={{
              position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)',
              pointerEvents: 'none', color: 'var(--color-muted)', fontSize: '0.75rem',
            }}>▾</span>
          </div>

          {/* Outside Kenya */}
          <div style={{ position: 'relative' }}>
            <select
              value={locCountry}
              onChange={e => {
                const opt = question.locationOptions!.countries.find(c => c.key === e.target.value)
                if (opt) { setLocCountry(opt.key); handleSingleSelect(opt.key, opt.label) }
              }}
              style={{
                width: '100%', padding: '0.875rem 2.5rem 0.875rem 1rem',
                background: 'var(--color-white)',
                border: `1.5px solid ${locCountry ? 'var(--color-teal)' : 'rgba(148,163,184,0.3)'}`,
                borderRadius: 'var(--radius-xl)',
                fontFamily: 'var(--font-sans)', fontSize: '0.925rem',
                color: locCountry ? 'var(--color-deep-teal)' : 'var(--color-slate)',
                cursor: 'pointer', appearance: 'none', outline: 'none',
                minHeight: '48px',
              }}
            >
              <option value="">Outside Kenya — select country…</option>
              {question.locationOptions.countries.map(c => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <span style={{
              position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)',
              pointerEvents: 'none', color: 'var(--color-muted)', fontSize: '0.75rem',
            }}>▾</span>
          </div>
        </motion.div>
      )}

      {/* Options — single and multi */}
      <div
        role={question.type === 'single' ? 'radiogroup' : 'group'}
        aria-label={question.text}
        style={{
          display: question.type === 'number' || question.type === 'location' ? 'none' : 'flex',
          flexDirection: 'column',
          gap: '0.625rem',
          flex: 1,
          maxWidth: '600px',
        }}
      >
        <AnimatePresence>
          {question.options.map((option, idx) => {
            const isSelected = question.type === 'single'
              ? selectedKey === option.key
              : multiSelected.has(option.key)

            const isFaded = question.type === 'single' && selectedKey && !isSelected

            return (
              <motion.button
                key={option.key}
                role={question.type === 'single' ? 'radio' : 'checkbox'}
                aria-checked={isSelected}
                initial={{ opacity: 0, y: 10 }}
                animate={{
                  opacity: isFaded ? 0.4 : 1,
                  y: 0,
                  scale: isSelected ? 1.01 : 1,
                }}
                transition={{
                  opacity: { duration: 0.3 },
                  y: { duration: 0.3, delay: idx * 0.04 },
                  scale: { duration: 0.15 },
                }}
                whileHover={!selectedKey || question.type === 'multi'
                  ? { scale: 1.01, borderColor: 'var(--color-teal)' }
                  : {}
                }
                whileTap={{ scale: 0.99 }}
                onClick={() => {
                  if (question.type === 'single') {
                    handleSingleSelect(option.key, option.label)
                  } else {
                    onMultiToggle(option.key, option.label)
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.875rem 1rem',
                  background: isSelected ? 'rgba(13, 148, 136, 0.06)' : 'var(--color-white)',
                  border: `1.5px solid ${isSelected ? 'var(--color-teal)' : 'rgba(148,163,184,0.3)'}`,
                  borderRadius: 'var(--radius-xl)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'var(--font-sans)',
                  fontSize: '0.925rem',
                  color: isSelected ? 'var(--color-deep-teal)' : 'var(--color-slate)',
                  fontWeight: isSelected ? 500 : 400,
                  minHeight: '48px',
                  transition: 'background 0.2s, border-color 0.2s, color 0.2s',
                  lineHeight: 1.4,
                }}
              >
                {/* Indicator */}
                <span style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: question.type === 'single' ? '50%' : '4px',
                  border: `2px solid ${isSelected ? 'var(--color-teal)' : 'rgba(148,163,184,0.5)'}`,
                  background: isSelected ? 'var(--color-teal)' : 'transparent',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s, border-color 0.2s',
                }}>
                  {isSelected && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>

                <span style={{ flex: 1 }}>{option.label}</span>
              </motion.button>
            )
          })}
        </AnimatePresence>
      </div>

      {/* Multi-select Continue button */}
      {question.type === 'multi' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: hasMultiSelection ? 1 : 0.4, y: 0 }}
          style={{ marginTop: '1.5rem', maxWidth: '600px' }}
        >
          <motion.button
            whileHover={hasMultiSelection ? { scale: 1.02 } : {}}
            whileTap={hasMultiSelection ? { scale: 0.98 } : {}}
            onClick={() => {
              if (hasMultiSelection) onMultiConfirm()
            }}
            disabled={!hasMultiSelection}
            style={{
              width: '100%',
              padding: '0.875rem',
              background: hasMultiSelection ? 'var(--color-deep-teal)' : 'var(--color-muted)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-xl)',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: hasMultiSelection ? 'pointer' : 'not-allowed',
              fontFamily: 'var(--font-sans)',
              transition: 'background 0.2s',
            }}
          >
            {hasMultiSelection
              ? `Continue (${multiSelected.size} selected) →`
              : 'Select at least one option'
            }
          </motion.button>
        </motion.div>
      )}
    </div>
  )
}
